import { useState, useEffect } from 'react';
import { Card, Badge, Button, Select } from '@atoms/index';
import { DetailModal, AlertModal } from '@organisms/index';
import { useAdminCustomers, useCustomerSegments } from '@/hooks/useAdminCustomers';
import { adminCustomerService, CustomerQuery, CustomerProfile } from '@services/adminCustomerService';
import { formatCurrency } from '@/utils/currency';
import { cn } from '@/utils/cn';

export default function CustomersTab() {
  const [filters, setFilters] = useState<CustomerQuery>({
    page: 1,
    limit: 20,
    segment: 'all',
    sortBy: 'createdAt',
  });
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerProfile | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [alertState, setAlertState] = useState<{ isOpen: boolean; title: string; message: string }>({ isOpen: false, title: '', message: '' });

  const { customers, total, isLoading, error } = useAdminCustomers(filters);
  const { segments } = useCustomerSegments();

  const handleViewDetails = async (customerId: string) => {
    try {
      const customer = await adminCustomerService.getCustomerDetails(customerId);
      setSelectedCustomer(customer);
      setIsDetailModalOpen(true);
    } catch (error) {
      console.error('Failed to load customer details:', error);
      setAlertState({ isOpen: true, title: 'Hata', message: 'Müşteri detayları yüklenemedi' });
    }
  };

  const handleSegmentFilter = (segment: CustomerQuery['segment']) => {
    setFilters((prev) => ({
      ...prev,
      segment: segment || 'all',
      page: 1,
    }));
  };

  const handleSearch = (search: string) => {
    setFilters((prev) => ({
      ...prev,
      search: search || undefined,
      page: 1,
    }));
  };

  const totalPages = Math.ceil(total / (filters.limit || 20));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Müşteri Yönetimi</h2>
          <p className="text-gray-600">Müşterileri görüntüleyin ve analiz edin</p>
        </div>
      </div>

      {segments && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <div className="text-center">
              <p className="text-2xl font-bold text-primary">{segments.vip}</p>
              <p className="text-sm text-gray-600">VIP Müşteriler</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-2xl font-bold text-info">{segments.new}</p>
              <p className="text-sm text-gray-600">Yeni Müşteriler</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-2xl font-bold text-warning">{segments.inactive}</p>
              <p className="text-sm text-gray-600">Pasif Müşteriler</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-2xl font-bold text-success">{segments.repeat}</p>
              <p className="text-sm text-gray-600">Tekrar Eden</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-2xl font-bold text-danger">{segments.churned}</p>
              <p className="text-sm text-gray-600">Kayıp Müşteriler</p>
            </div>
          </Card>
        </div>
      )}

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <Select
            label="Segment"
            value={filters.segment || 'all'}
            onChange={(value) => handleSegmentFilter(value as CustomerQuery['segment'])}
            options={[
              { value: 'all', label: 'Tümü' },
              { value: 'vip', label: 'VIP' },
              { value: 'new', label: 'Yeni' },
              { value: 'inactive', label: 'Pasif' },
              { value: 'repeat', label: 'Tekrar Eden' },
              { value: 'churned', label: 'Kayıp' },
            ]}
            size="md"
          />

          <Select
            label="Sıralama"
            value={filters.sortBy || 'createdAt'}
            onChange={(value) =>
              setFilters((prev) => ({
                ...prev,
                sortBy: value as CustomerQuery['sortBy'],
              }))
            }
            options={[
              { value: 'createdAt', label: 'Kayıt Tarihi' },
              { value: 'totalSpent', label: 'Toplam Harcama' },
              { value: 'orderCount', label: 'Sipariş Sayısı' },
              { value: 'lastOrderDate', label: 'Son Sipariş' },
            ]}
            size="md"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ara</label>
            <input
              type="text"
              placeholder="İsim, email, telefon..."
              value={filters.search || ''}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full px-5 py-3 border border-gray-200 rounded-2xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary hover:border-gray-300 bg-white placeholder:text-gray-400"
            />
          </div>
        </div>
      </Card>

      {isLoading && (
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-gray-600">Müşteriler yükleniyor...</p>
        </div>
      )}

      {error && (
        <Card className="bg-warning/10 border border-warning/20">
          <p className="text-warning">{error}</p>
        </Card>
      )}

      {!isLoading && !error && (
        <>
          <Card className="p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left font-semibold px-6 py-3">Müşteri</th>
                    <th className="text-left font-semibold px-6 py-3">İletişim</th>
                    <th className="text-right font-semibold px-6 py-3">Toplam Harcama</th>
                    <th className="text-right font-semibold px-6 py-3">Sipariş Sayısı</th>
                    <th className="text-right font-semibold px-6 py-3">Ortalama</th>
                    <th className="text-left font-semibold px-6 py-3">Son Sipariş</th>
                    <th className="text-right font-semibold px-6 py-3">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {customers.length === 0 ? (
                    <tr>
                      <td className="px-6 py-10 text-center text-gray-500" colSpan={7}>
                        Müşteri bulunamadı.
                      </td>
                    </tr>
                  ) : (
                    customers.map((customer) => (
                      <tr key={customer.id} className="hover:bg-gray-50">
                        <td className="px-6 py-3">
                          <div>
                            <p className="font-medium text-gray-900">
                              {customer.name || customer.surname
                                ? `${customer.name || ''} ${customer.surname || ''}`.trim()
                                : customer.email?.split('@')[0] || 'Müşteri'}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(customer.createdAt).toLocaleDateString('tr-TR')}
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-3 text-gray-700">
                          <p className="text-sm">{customer.email}</p>
                          {customer.phone && <p className="text-xs text-gray-500">{customer.phone}</p>}
                        </td>
                        <td className="px-6 py-3 text-right font-semibold text-primary">
                          {formatCurrency(customer.totalSpent)}
                        </td>
                        <td className="px-6 py-3 text-right text-gray-700">{customer.orderCount}</td>
                        <td className="px-6 py-3 text-right text-gray-600">
                          {formatCurrency(customer.avgOrderValue)}
                        </td>
                        <td className="px-6 py-3 text-gray-600">
                          {customer.lastOrderDate
                            ? new Date(customer.lastOrderDate).toLocaleDateString('tr-TR')
                            : '—'}
                        </td>
                        <td className="px-6 py-3 text-right">
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => handleViewDetails(customer.id)}
                          >
                            Detay
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {totalPages > 1 && (
            <div className="flex justify-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setFilters((prev) => ({ ...prev, page: Math.max(1, (prev.page || 1) - 1) }))}
                disabled={(filters.page || 1) === 1}
              >
                ← Önceki
              </Button>
              <span className="px-4 py-2 text-gray-700">
                Sayfa {filters.page || 1} / {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setFilters((prev) => ({ ...prev, page: Math.min(totalPages, (prev.page || 1) + 1) }))}
                disabled={(filters.page || 1) === totalPages}
              >
                Sonraki →
              </Button>
            </div>
          )}
        </>
      )}

      {isDetailModalOpen && selectedCustomer && (
        <CustomerDetailModal
          customer={selectedCustomer}
          onClose={() => {
            setIsDetailModalOpen(false);
            setSelectedCustomer(null);
          }}
        />
      )}

      <AlertModal
        isOpen={alertState.isOpen}
        title={alertState.title}
        message={alertState.message}
        variant="danger"
        onClose={() => setAlertState((a) => ({ ...a, isOpen: false }))}
      />
    </div>
  );
}

interface CustomerDetailModalProps {
  customer: CustomerProfile;
  onClose: () => void;
}

function CustomerDetailModal({ customer, onClose }: CustomerDetailModalProps) {
  const [orders, setOrders] = useState<any[]>([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);

  useEffect(() => {
    const loadOrders = async () => {
      setIsLoadingOrders(true);
      try {
        const result = await adminCustomerService.getCustomerOrders(customer.id, 1, 10);
        setOrders(result.orders);
      } catch (error) {
        console.error('Failed to load orders:', error);
      } finally {
        setIsLoadingOrders(false);
      }
    };

    loadOrders();
  }, [customer.id]);

  const customerName = customer.name || customer.surname
    ? `${customer.name || ''} ${customer.surname || ''}`.trim()
    : customer.email?.split('@')[0] || 'Müşteri';

  const statCards = [
    {
      label: 'Toplam Harcama',
      value: formatCurrency(customer.totalSpent),
      icon: '💰',
      iconBgColor: 'bg-green-50',
      valueColor: 'text-[#E63946]',
    },
    {
      label: 'Sipariş Sayısı',
      value: customer.orderCount.toString(),
      icon: '📦',
      iconBgColor: 'bg-blue-50',
      valueColor: 'text-[#2A9D8F]',
    },
    {
      label: 'Ortalama Sipariş',
      value: formatCurrency(customer.avgOrderValue),
      icon: '📊',
      iconBgColor: 'bg-orange-50',
      valueColor: 'text-warning',
    },
  ];

  const sections = [
    {
      title: 'İletişim Bilgileri',
      icon: '👤',
      iconBgColor: 'bg-[#2A9D8F]/10',
      gridCols: 2 as const,
      content: (
        <>
          <div>
            <p className="text-xs text-gray-500 mb-1">Email</p>
            <p className="text-gray-900 font-medium">{customer.email}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Telefon</p>
            <p className="text-gray-900 font-medium">{customer.phone || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Kayıt Tarihi</p>
            <p className="text-gray-900 font-medium">
              {new Date(customer.createdAt).toLocaleDateString('tr-TR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Son Sipariş</p>
            <p className="text-gray-900 font-medium">
              {customer.lastOrderDate
                ? new Date(customer.lastOrderDate).toLocaleDateString('tr-TR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })
                : '—'}
            </p>
          </div>
        </>
      ),
    },
    {
      title: 'Sipariş Geçmişi',
      icon: '🛒',
      iconBgColor: 'bg-green-50',
      gridCols: 1 as const,
      content: (
        <div className="space-y-3">
          {isLoadingOrders ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            </div>
          ) : orders.length === 0 ? (
            <p className="text-gray-500 text-center py-4">Sipariş bulunamadı</p>
          ) : (
            orders.map((order) => (
              <div
                key={order.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <div>
                  <p className="font-medium text-gray-900">
                    Sipariş #{order.orderNumber || order.id.slice(0, 8).toUpperCase()}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    {new Date(order.createdAt).toLocaleString('tr-TR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-[#E63946] mb-1">
                    {formatCurrency(
                      typeof order.total === 'number' && !isNaN(order.total)
                        ? order.total
                        : (order.subtotal || 0) + (order.deliveryFee || 0) + (order.serviceFee || 0) || 0
                    )}
                  </p>
                  <Badge
                    variant={
                      order.status === 'DELIVERED'
                        ? 'success'
                        : order.status === 'CANCELLED'
                        ? 'danger'
                        : 'warning'
                    }
                  >
                    {order.status}
                  </Badge>
                </div>
              </div>
            ))
          )}
        </div>
      ),
    },
    ...(customer.favoriteRestaurants.length > 0
      ? [
          {
            title: 'En Çok Sipariş Verdiği Restoranlar',
            icon: '🍽️',
            iconBgColor: 'bg-orange-50',
            gridCols: 1 as const,
            content: (
              <div className="space-y-3">
                {customer.favoriteRestaurants.map((restaurant, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                  >
                    <span className="font-medium text-gray-900">{restaurant.restaurantName}</span>
                    <span className="text-gray-600 font-medium">{restaurant.orderCount} sipariş</span>
                  </div>
                ))}
              </div>
            ),
          },
        ]
      : []),
    ...(customer.addresses.length > 0
      ? [
          {
            title: 'Adres Defteri',
            icon: '📍',
            iconBgColor: 'bg-red-50',
            gridCols: 1 as const,
            content: (
              <div className="space-y-3">
                {customer.addresses.map((addr) => (
                  <div key={addr.id} className="p-4 bg-gray-50 rounded-xl">
                    <p className="font-medium text-gray-900 mb-1">{addr.title}</p>
                    <p className="text-sm text-gray-600">{addr.fullAddress}</p>
                  </div>
                ))}
              </div>
            ),
          },
        ]
      : []),
    ...(customer.reviews.length > 0
      ? [
          {
            title: 'Yorumlar',
            icon: '⭐',
            iconBgColor: 'bg-yellow-50',
            gridCols: 1 as const,
            content: (
              <div className="space-y-3">
                {customer.reviews.map((review) => (
                  <div key={review.id} className="p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-warning font-semibold">⭐ {review.rating}</span>
                      <span className="text-xs text-gray-500">
                        {new Date(review.createdAt).toLocaleDateString('tr-TR')}
                      </span>
                    </div>
                    {review.comment && <p className="text-sm text-gray-700">{review.comment}</p>}
                  </div>
                ))}
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <DetailModal
      title={`Müşteri: ${customerName}`}
      subtitle={new Date(customer.createdAt).toLocaleDateString('tr-TR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })}
      onClose={onClose}
      statCards={statCards}
      sections={sections}
    />
  );
}
